import api from '@/api';
import { dateFormat, saveParamsToQuery } from '@/utils/helpers';

let currentTicketListRequest = null;

const protocols = {
  namespaced: true,

  state() {
    return {
      isLoading: true,
      filterParams: {
        objectName: '',
        region: '',
        number: '',
        date: '',
        customerName: '',
      },
      filterParamsHistory: [],
      search: '',
      list: {
        protocols: [],
        pageCount: 0,
        page: 0,
      },
      protocolDetailErrorText: '',
      protocolDetail: null,
    };
  },

  mutations: {
    setFilterParams(state, params) {
      state.filterParams = params.result;
    },

    setPage(state, page) {
      state.list.page = page;
    },

    setSelectedFilters(state, params) {
      state.selectedFilters = params;
    },

    setSelectedFiltersHistory(state, params) {
      state.selectedFiltersHistory = params;
    },

    clearSelectedFiltersHistory(state) {
      state.selectedFiltersHistory = {};
    },

    setSearch(state, value) {
      state.search = value;
    },

    setList(state, data) {
      state.list = { ...data.result };
    },

    changeLoading(state, status) {
      state.isLoading = status;
    },

    setProtocolDetail(state, value) {
      state.protocolDetail = value;
    },

    setProtocolDetailErrorText(state, value) {
      state.protocolDetailErrorText = value;
    },
  },

  actions: {
    async setSearch({ commit, dispatch }, value) {
      commit('setSearch', value);
      commit('setPage', 1);
      await dispatch('loadList');
    },
    async setPage({ commit, dispatch }, page) {
      commit('setPage', page);
      await dispatch('loadList');
    },
    async loadList({
      state, commit, rootState, rootGetters,
    }) {
      commit('changeLoading', true);

      try {
        if (currentTicketListRequest) {
          return;
        }
        const { isLoggedIn } = rootState.user;
        const role = rootGetters['user/getPriorityRole']('supplier');
        if (!role && isLoggedIn) {
          return;
        }

        const params = {
          pageSize: 10,
        };

        currentTicketListRequest = api.protocols[role].list(params);
        const { data } = await currentTicketListRequest;
        saveParamsToQuery({ page: params.page, search: params.search });
        commit('setList', data);
        currentTicketListRequest = null;
      } catch (e) {
        currentTicketListRequest = null;
        console.log(e);
      } finally {
        commit('changeLoading', false);
      }
    },

    async loadProtocolDetail({ commit, rootState, rootGetters }, { id }) {
      commit('setProtocolDetail', null);
      commit('changeLoading', true);

      try {
        const { isLoggedIn } = rootState.user;
        const role = rootGetters['user/getPriorityRole']('supplier');

        if (!role && isLoggedIn) {
          return;
        }
        const { data } = await api.protocols[role].detail({ id });
        commit('setProtocolDetail', data.result);
      } catch (e) {
        console.log(e);
        commit('changeLoading', false);
      } finally {
        commit('changeLoading', false);
      }
    },
    async submitProtocol({ commit, rootState, rootGetters }, { id, type = 'reject' }) {
      commit('changeLoading', true);
      try {
        const { isLoggedIn } = rootState.user;
        const role = rootGetters['user/getPriorityRole']('supplier');

        if (!role && isLoggedIn) {
          return;
        }

        if (type === 'reject') {
          await api.protocols[role].reject({ id });
        } else {
          await api.protocols[role].accept({ id });
        }
      } catch (e) {
        console.log(e);
        if (e.response.data?.description) {
          commit('setProtocolDetailErrorText', e.response.data?.description);
        }
      } finally {
        commit('changeLoading', false);
      }
    },
  },
  getters: {
    protocolDetail(state) {
      if (!state.protocolDetail) {
        return {
          info: [],
          files: [],
        };
      }

      return {
        info: [
          {
            name: 'Сведения о протоколе',
            items: [
              { value: state.protocolDetail.protocol.documentNumber, name: 'Номер протокола' },
              {
                value: dateFormat(state.protocolDetail.protocol.documentDate),
                name: 'Дата публикации',
              },
              { value: state.protocolDetail.protocol.status, name: 'Статус протокола' },
            ],
          }, {
            name: 'Сведения о закупке',
            items: [
              { value: state.protocolDetail.lot.number, name: 'Номер закупки' },
              { value: state.protocolDetail.lot.name, name: 'Наименование закупки' },
              {
                value: dateFormat(state.protocolDetail.lot.documentDate),
                name: 'Дата публикации',
              },
              {
                value: dateFormat(state.protocolDetail.lot.updatedAt),
                name: 'Дата изменения',
              },
            ],
          },
          {

            name: 'Сведения о заявителе',
            items: [
              { value: state.protocolDetail.customer.title, name: 'Полное наименование' },
              { value: state.protocolDetail.customer.inn, name: 'ИНН' },
              { value: state.protocolDetail.customer.kpp, name: 'КПП' },
              { value: state.protocolDetail.customer.ogrn, name: 'ОГРН' },
              { value: state.protocolDetail.customer.legalAddress, name: 'Юридический адрес' },
              { value: state.protocolDetail.customer.postalAddress, name: 'Почтовый адрес' },
            ],
          },
          {
            name: 'Сведения о поставщике',
            items: [
              { value: state.protocolDetail.supplier.title, name: 'Полное наименование' },
              { value: state.protocolDetail.supplier.inn, name: 'ИНН' },
              { value: state.protocolDetail.supplier.kpp, name: 'КПП' },
              { value: state.protocolDetail.supplier.ogrn, name: 'ОГРН' },
              { value: state.protocolDetail.supplier.legalAddress, name: 'Юридический адрес' },
              { value: state.protocolDetail.supplier.postalAddress, name: 'Почтовый адрес' },
            ],
          },
        ],
        files: state.protocolDetail.files && state.protocolDetail.files.length > 0 ? state.protocolDetail.files : [],
      };
    },
    protocolDetailItems(state) {
      if (!state.protocolDetail || !state.protocolDetail.items || state.protocolDetail.items.length < 1) {
        return [];
      }

      return state.protocolDetail.items.map((item) => ({
        ...item,
        objectTitle: state.protocolDetail.object.title ?? '',
        objectAddress: state.protocolDetail.object.address ?? '',
      }));
    },
    protocolDetailNumber(state) {
      return state.protocolDetail?.protocol?.documentNumber;
    },
    protocolDetailIsNeedHandle(state) {
      return ['Протокол выгружен', 'Требуется подтверждение']
        .indexOf(state.protocolDetail?.protocol.status) !== -1;
    },
  },
};

export default protocols;
